/**
 * CBO Custom Data Entry Component
 * Form for entering CBO custom SDOH data for members
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Save,
  User,
  Users,
  Languages,
  Heart,
  Home,
  Pill,
  FileText,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface Member {
  member_id: number;
  first_name: string;
  last_name: string;
  risk_level?: string;
}

interface CBODataEntryProps {
  memberId: number;
  cboId: number;
  onSave?: () => void;
  onCancel?: () => void;
  onDataSubmit?: () => void;
}

interface CBODataForm {
  // Population focus
  population_focus: string[];
  cultural_community: string;
  special_population_flags: Record<string, boolean>;

  // Language
  primary_language: string;
  preferred_language: string;
  language_barrier_flag: boolean;
  interpreter_needed: boolean;
  preferred_communication_method: string;

  // Social risk factors
  social_risk_factors: Record<string, string>;
  behavioral_health_risks: Record<string, boolean>;
  safety_concerns: Record<string, boolean>;

  // Family dynamics
  family_dynamics: Record<string, any>;

  // Clinical indicators
  chronic_conditions: string[];
  clinical_risk_factors: Record<string, boolean>;
  healthcare_utilization_pattern: Record<string, number>;

  // Medications
  medications: Array<{ name: string; adherence: number }>;
  medication_adherence_score: number;
  medication_barriers: string[];
  pharmacy_preference: string;

  // Case notes
  case_notes_summary: string;
}

const POPULATION_FOCUS_OPTIONS = [
  'seniors',
  'veterans',
  'families',
  'youth',
  'homeless',
  'immigrants',
  'refugees',
  'lgbtq+',
  'disabled',
  'low_income',
];

// English and Spanish first; remaining common healthcare languages alphabetical.
// Values are ISO 639-1 codes (≤10 chars) matching the ml_cbo_integration schema.
const LANGUAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'en',    label: 'English' },
  { value: 'es',    label: 'Spanish' },
  { value: 'ar',    label: 'Arabic' },
  { value: 'bn',    label: 'Bengali' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'fr',    label: 'French' },
  { value: 'de',    label: 'German' },
  { value: 'gu',    label: 'Gujarati' },
  { value: 'ht',    label: 'Haitian Creole' },
  { value: 'hi',    label: 'Hindi' },
  { value: 'id',    label: 'Indonesian' },
  { value: 'it',    label: 'Italian' },
  { value: 'ja',    label: 'Japanese' },
  { value: 'km',    label: 'Khmer (Cambodian)' },
  { value: 'ko',    label: 'Korean' },
  { value: 'mr',    label: 'Marathi' },
  { value: 'my',    label: 'Burmese (Myanmar)' },
  { value: 'fa',    label: 'Persian (Farsi)' },
  { value: 'pl',    label: 'Polish' },
  { value: 'pt',    label: 'Portuguese' },
  { value: 'pa',    label: 'Punjabi' },
  { value: 'ru',    label: 'Russian' },
  { value: 'so',    label: 'Somali' },
  { value: 'sw',    label: 'Swahili' },
  { value: 'tl',    label: 'Tagalog (Filipino)' },
  { value: 'ta',    label: 'Tamil' },
  { value: 'te',    label: 'Telugu' },
  { value: 'th',    label: 'Thai' },
  { value: 'tr',    label: 'Turkish' },
  { value: 'uk',    label: 'Ukrainian' },
  { value: 'ur',    label: 'Urdu' },
  { value: 'vi',    label: 'Vietnamese' },
  { value: 'other', label: 'Other' },
];

const CHRONIC_CONDITIONS = [
  'diabetes',
  'hypertension',
  'asthma',
  'copd',
  'heart_disease',
  'depression',
  'anxiety',
  'arthritis',
  'obesity',
  'substance_use',
];

export const CBODataEntry: React.FC<CBODataEntryProps> = ({
  memberId: propMemberId,
  cboId,
  onSave,
  onCancel,
  onDataSubmit,
}) => {
  const { token: contextToken } = useAuth();

  // Helper function to get auth token
  const getAuthToken = (): string | null => {
    if (typeof window === 'undefined') return null;
    return contextToken || localStorage.getItem('auth_token');
  };

  // Member selection state (for standalone usage)
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number>(propMemberId || 0);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Use prop memberId if provided, otherwise use selected
  const memberId = propMemberId && propMemberId > 0 ? propMemberId : selectedMemberId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<CBODataForm>({
    population_focus: [],
    cultural_community: '',
    special_population_flags: {},
    primary_language: 'en',
    preferred_language: 'en',
    language_barrier_flag: false,
    interpreter_needed: false,
    preferred_communication_method: 'sms',
    social_risk_factors: {},
    behavioral_health_risks: {},
    safety_concerns: {},
    family_dynamics: {},
    chronic_conditions: [],
    clinical_risk_factors: {},
    healthcare_utilization_pattern: {},
    medications: [],
    medication_adherence_score: 100,
    medication_barriers: [],
    pharmacy_preference: '',
    case_notes_summary: '',
  });

  // Fetch members for dropdown when no memberId is provided
  useEffect(() => {
    const authToken = getAuthToken();
    // Fetch members when no valid memberId is provided and we have auth
    if ((!propMemberId || propMemberId <= 0) && authToken) {
      fetchMembers();
    }
  }, [propMemberId, cboId, contextToken]);

  const fetchMembers = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      setLoadingMembers(true);
      const activeCboId = cboId || localStorage.getItem('active_cbo_id') || '1';
      const response = await authFetch(`${getWeaveinApiUrl()}/api/retention/all-members/${activeCboId}?page=1&page_size=500`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || data || []);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (memberId && memberId > 0 && cboId && cboId > 0) {
      loadExistingData();
    } else {
      setLoading(false);
    }
  }, [memberId, cboId]);

  const loadExistingData = async () => {
    if (!memberId || memberId <= 0 || !cboId || cboId <= 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const authToken = getAuthToken();
      if (!authToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await authFetch(`${getWeaveinApiUrl()}/api/integrations/cbo/${cboId}/custom-data/${memberId}`);

      if (response.ok) {
        const data = await response.json();
        if (data) {
          setFormData({
            population_focus: data.population_focus || [],
            cultural_community: data.cultural_community || '',
            special_population_flags: data.special_population_flags || {},
            primary_language: data.primary_language || 'en',
            preferred_language: data.preferred_language || 'en',
            language_barrier_flag: data.language_barrier_flag || false,
            interpreter_needed: data.interpreter_needed || false,
            preferred_communication_method: data.preferred_communication_method || 'sms',
            social_risk_factors: data.social_risk_factors || {},
            behavioral_health_risks: data.behavioral_health_risks || {},
            safety_concerns: data.safety_concerns || {},
            family_dynamics: data.family_dynamics || {},
            chronic_conditions: data.chronic_conditions || [],
            clinical_risk_factors: data.clinical_risk_factors || {},
            healthcare_utilization_pattern: data.healthcare_utilization_pattern || {},
            medications: data.medications || [],
            medication_adherence_score: data.medication_adherence_score || 100,
            medication_barriers: data.medication_barriers || [],
            pharmacy_preference: data.pharmacy_preference || '',
            case_notes_summary: data.case_notes_summary || '',
          });
        }
      }
    } catch (err) {
      console.error('Error loading existing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setError('Authentication required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const response = await authFetch(`${getWeaveinApiUrl()}/api/integrations/cbo/${cboId}/custom-data`, {
        method: 'POST',
        jsonBody: {
            member_id: memberId,
            ...formData,
          }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save CBO data');
      }

      setSuccess(true);
      if (onSave) {
        setTimeout(() => {
          onSave();
        }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  const toggleArrayItem = (array: string[], item: string) => {
    if (array.includes(item)) {
      return array.filter((i) => i !== item);
    }
    return [...array, item];
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Show member selector when no member is selected
  if (!memberId || memberId <= 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            CBO Custom SDOH Data Entry
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-muted-foreground" />
              <Label htmlFor="member-select" className="text-base font-medium">
                Select a Member
              </Label>
            </div>

            {loadingMembers ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading members...</span>
              </div>
            ) : members.length > 0 ? (
              <Select
                value={selectedMemberId > 0 ? selectedMemberId.toString() : ''}
                onValueChange={(value) => setSelectedMemberId(parseInt(value))}
              >
                <SelectTrigger id="member-select" className="w-full max-w-md">
                  <SelectValue placeholder="Choose a member to enter SDOH data" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((member) => (
                    <SelectItem key={member.member_id} value={member.member_id.toString()}>
                      {member.first_name} {member.last_name}
                      {member.risk_level && (
                        <span className="ml-2 text-muted-foreground">({member.risk_level})</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-muted-foreground">No members found. Please ensure members are enrolled in your organization.</p>
            )}

            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">About CBO Data Entry</h4>
              <p className="text-sm text-muted-foreground">
                Enter custom SDOH data that enhances ML predictions:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                <li>Population focus and cultural community information</li>
                <li>Language preferences and communication needs</li>
                <li>Social risk factors and behavioral health indicators</li>
                <li>Clinical conditions and medication adherence data</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          CBO Custom SDOH Data Entry
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-4">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>Data saved successfully!</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="demographics" className="w-full">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
            <TabsList className="w-max min-w-full justify-start flex-nowrap">
              <TabsTrigger value="demographics" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">Demographics</TabsTrigger>
              <TabsTrigger value="language" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">Language</TabsTrigger>
              <TabsTrigger value="risks" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">Risk Factors</span>
                <span className="sm:hidden">Risks</span>
              </TabsTrigger>
              <TabsTrigger value="clinical" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">Clinical</TabsTrigger>
              <TabsTrigger value="medications" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
                <span className="hidden sm:inline">Medications</span>
                <span className="sm:hidden">Meds</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="demographics" className="space-y-4">
            <div>
              <Label>Population Focus</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {POPULATION_FOCUS_OPTIONS.map((option) => (
                  <Badge
                    key={option}
                    variant={formData.population_focus.includes(option) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        population_focus: toggleArrayItem(formData.population_focus, option),
                      })
                    }
                  >
                    {option}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="cultural_community">Cultural Community</Label>
              <Input
                id="cultural_community"
                value={formData.cultural_community}
                onChange={(e) =>
                  setFormData({ ...formData, cultural_community: e.target.value })
                }
                placeholder="e.g., Latino, African American, Asian"
              />
            </div>
          </TabsContent>

          <TabsContent value="language" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="primary_language">Primary Language</Label>
                <Select
                  value={formData.primary_language}
                  onValueChange={(value) =>
                    setFormData({ ...formData, primary_language: value })
                  }
                >
                  <SelectTrigger id="primary_language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="preferred_language">Preferred Language</Label>
                <Select
                  value={formData.preferred_language}
                  onValueChange={(value) =>
                    setFormData({ ...formData, preferred_language: value })
                  }
                >
                  <SelectTrigger id="preferred_language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="language_barrier"
                checked={formData.language_barrier_flag}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, language_barrier_flag: checked === true })
                }
              />
              <Label htmlFor="language_barrier" className="cursor-pointer">
                Language Barrier Present
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="interpreter_needed"
                checked={formData.interpreter_needed}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, interpreter_needed: checked === true })
                }
              />
              <Label htmlFor="interpreter_needed" className="cursor-pointer">
                Interpreter Needed
              </Label>
            </div>

            <div>
              <Label htmlFor="preferred_communication">Preferred Communication Method</Label>
              <Select
                value={formData.preferred_communication_method}
                onValueChange={(value) =>
                  setFormData({ ...formData, preferred_communication_method: value })
                }
              >
                <SelectTrigger id="preferred_communication">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="in_person">In Person</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          <TabsContent value="risks" className="space-y-4">
            <div>
              <Label>Social Risk Factors</Label>
              <div className="space-y-2 mt-2">
                {['housing', 'food', 'transportation', 'financial', 'employment'].map((factor) => (
                  <div key={factor} className="flex items-center gap-2">
                    <Label className="w-32 capitalize">{factor}</Label>
                    <Input
                      value={formData.social_risk_factors[factor] || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          social_risk_factors: {
                            ...formData.social_risk_factors,
                            [factor]: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g., unstable, insecure, barrier"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Behavioral Health Risks</Label>
              <div className="space-y-2 mt-2">
                {['depression', 'anxiety', 'substance_use', 'ptsd', 'suicidal_ideation'].map(
                  (risk) => (
                    <div key={risk} className="flex items-center gap-2">
                      <Checkbox
                        id={risk}
                        checked={formData.behavioral_health_risks[risk] || false}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            behavioral_health_risks: {
                              ...formData.behavioral_health_risks,
                              [risk]: checked === true,
                            },
                          })
                        }
                      />
                      <Label htmlFor={risk} className="cursor-pointer capitalize">
                        {risk.replace('_', ' ')}
                      </Label>
                    </div>
                  )
                )}
              </div>
            </div>

            <div>
              <Label>Safety Concerns</Label>
              <div className="space-y-2 mt-2">
                {['domestic_violence', 'elder_abuse', 'child_abuse', 'unsafe_housing'].map(
                  (concern) => (
                    <div key={concern} className="flex items-center gap-2">
                      <Checkbox
                        id={concern}
                        checked={formData.safety_concerns[concern] || false}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            safety_concerns: {
                              ...formData.safety_concerns,
                              [concern]: checked === true,
                            },
                          })
                        }
                      />
                      <Label htmlFor={concern} className="cursor-pointer capitalize">
                        {concern.replace('_', ' ')}
                      </Label>
                    </div>
                  )
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="clinical" className="space-y-4">
            <div>
              <Label>Chronic Conditions</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {CHRONIC_CONDITIONS.map((condition) => (
                  <Badge
                    key={condition}
                    variant={
                      formData.chronic_conditions.includes(condition) ? 'default' : 'outline'
                    }
                    className="cursor-pointer"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        chronic_conditions: toggleArrayItem(formData.chronic_conditions, condition),
                      })
                    }
                  >
                    {condition.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Healthcare Utilization Pattern</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <Label htmlFor="er_visits">ER Visits (12m)</Label>
                  <Input
                    id="er_visits"
                    type="number"
                    value={formData.healthcare_utilization_pattern.er_visits || 0}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        healthcare_utilization_pattern: {
                          ...formData.healthcare_utilization_pattern,
                          er_visits: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="pcp_visits">PCP Visits (12m)</Label>
                  <Input
                    id="pcp_visits"
                    type="number"
                    value={formData.healthcare_utilization_pattern.pcp_visits || 0}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        healthcare_utilization_pattern: {
                          ...formData.healthcare_utilization_pattern,
                          pcp_visits: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="medications" className="space-y-4">
            <div>
              <Label htmlFor="medication_adherence">Medication Adherence Score (0-100)</Label>
              <Input
                id="medication_adherence"
                type="number"
                min="0"
                max="100"
                value={formData.medication_adherence_score}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    medication_adherence_score: parseInt(e.target.value) || 100,
                  })
                }
              />
            </div>

            <div>
              <Label>Medication Barriers</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['cost', 'access', 'side_effects', 'complexity', 'forgetfulness'].map(
                  (barrier) => (
                    <Badge
                      key={barrier}
                      variant={
                        formData.medication_barriers.includes(barrier) ? 'default' : 'outline'
                      }
                      className="cursor-pointer"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          medication_barriers: toggleArrayItem(
                            formData.medication_barriers,
                            barrier
                          ),
                        })
                      }
                    >
                      {barrier}
                    </Badge>
                  )
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="pharmacy_preference">Pharmacy Preference</Label>
              <Input
                id="pharmacy_preference"
                value={formData.pharmacy_preference}
                onChange={(e) =>
                  setFormData({ ...formData, pharmacy_preference: e.target.value })
                }
                placeholder="e.g., CVS, Walgreens, Local Pharmacy"
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-6">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Data'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CBODataEntry;
